/**
 * BlockRenderer — Renders an array of ContentBlocks from content.json.
 *
 * Supports: text, callout, code, sub-section, reading-list, support, card.
 * Sub-sections recurse: they contain their own blocks array.
 *
 * This component contains ZERO content. All text comes from content.json.
 */

import type { ContentBlock, SupportItem } from '../types';
import SegmentRenderer from './SegmentRenderer';
import Callout from './Callout';
import CodeBlock from './CodeBlock';

interface BlockRendererProps {
  blocks: ContentBlock[];
}

/**
 * Renders a single support contact card.
 */
function SupportCard({ item }: { item: SupportItem }) {
  const isWeb = item.type === 'web';
  const isPhone = item.type === 'phone';

  return (
    <div className="flex flex-col md:flex-row gap-6 items-center justify-between p-6 rounded-2xl bg-slate-950 border border-slate-800 hover:border-slate-700 transition-colors">
      <div className="text-center md:text-left">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
          {item.label}
        </div>
        <div className={`font-bold leading-tight ${isWeb ? 'text-emerald-400/80 text-sm sm:text-lg break-all' : 'text-emerald-400 text-lg'}`}>
          <a href={item.href} className="hover:underline">{item.value}</a>
        </div>
        {isPhone && item.secondaryValue && (
          <div className="text-emerald-400/80 font-bold text-sm leading-tight mt-1">
            {item.secondaryLabel}: <a href={item.secondaryHref} className="hover:underline">{item.secondaryValue}</a>
          </div>
        )}
      </div>
      <div className={`flex flex-col sm:flex-row gap-3 w-full md:w-auto`}>
        <a
          href={item.href}
          className="flex-1 md:flex-none text-center px-6 py-3 rounded-full bg-slate-800 text-white font-bold text-sm hover:bg-slate-700 transition-colors"
          {...(isWeb ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {item.actionLabel}
        </a>
        {isPhone && item.secondaryActionLabel && (
          <a
            href={item.secondaryHref}
            className="flex-1 md:flex-none text-center px-6 py-3 rounded-full bg-slate-800 text-white font-bold text-sm hover:bg-slate-700 transition-colors"
          >
            {item.secondaryActionLabel}
          </a>
        )}
      </div>
    </div>
  );
}

export default function BlockRenderer({ blocks }: BlockRendererProps) {
  return (
    <>
      {blocks.map((block, i) => {
        switch (block.type) {
          case 'text':
            return (
              <p key={i} className="text-slate-400 leading-relaxed">
                <SegmentRenderer segments={block.segments} />
              </p>
            );

          case 'callout':
            return (
              <Callout key={i} type={block.style} title={block.title}>
                <SegmentRenderer segments={block.segments} />
              </Callout>
            );

          case 'code':
            return <CodeBlock key={i} language={block.language} code={block.code} />;

          case 'sub-section':
            return (
              <div key={i} id={block.id} className="scroll-mt-32 mb-16 last:mb-0">
                <h3 className="text-xl font-bold text-white mb-4">{block.title}</h3>
                <div className="space-y-4">
                  <BlockRenderer blocks={block.blocks} />
                </div>
              </div>
            );

          case 'reading-list':
            return (
              <ul key={i} className="reading-grid grid grid-cols-1 md:grid-cols-2 gap-4">
                {block.items.map((item) => (
                  <li key={item.title}>
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 rounded-xl bg-slate-900 border border-slate-800 hover:border-emerald-500/50 hover:bg-slate-800/50 transition-all group"
                    >
                      <div className="h-2 w-2 rounded-full bg-emerald-500 group-hover:scale-125 transition-transform" aria-hidden="true" />
                      <span className="text-slate-300 font-medium">{item.title}</span>
                      <svg className="ml-auto w-4 h-4 text-slate-600 group-hover:text-emerald-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </li>
                ))}
              </ul>
            );

          case 'support':
            return (
              <div key={i} className="support-card p-8 rounded-3xl bg-slate-900 border border-slate-800">
                <div className="space-y-4">
                  {block.items.map((item, j) => (
                    <SupportCard key={j} item={item} />
                  ))}
                </div>
              </div>
            );

          case 'card':
            return (
              <div key={i} className="p-8 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
                <BlockRenderer blocks={block.blocks} />
              </div>
            );

          default:
            return null;
        }
      })}
    </>
  );
}
